import { ApiRequestStatus } from '@/dataAccess/ApiRequestStatus'
import { configuredAxios } from '@/dataAccess/api'
import Service from '@/dataAccess/models/Service'
import Employee from '@/dataAccess/models/Employee'
import AccessibleClient from '@/dataAccess/models/AccessibleClient'

/**
 * Represent a single event in the calendar.
 */
export default class Action {
  readonly id: number
  readonly description: string // used by the vCalender component
  readonly start: Date // used by the vCalender component
  readonly end: Date // used by the vCalender component
  readonly color: string // used by the vCalender component
  readonly timed: boolean // used by the vCalender component
  readonly service: Service
  readonly priority: 10 | 20 | 30 | 40
  readonly name: string
  readonly priorityClass: string
  readonly employees: Array<Employee>
  readonly clients: Array<AccessibleClient>

  constructor(apiObject) {
    this.id = apiObject.id
    this.description = apiObject.description
    this.start = new Date(apiObject.start) // used by the vCalender component
    this.end = apiObject.end ? new Date(apiObject.end) : null // used by the vCalender component
    this.timed = apiObject.is_timed
    this.service = Service.find(apiObject.service)
    this.color = this.service ? this.service.color : '#cabec1' // used by the vCalender component
    this.priority = apiObject.priority
    this.employees = Employee.findIn(apiObject.employees)
    this.clients = AccessibleClient.findIn(apiObject.clients)

    this.name = this.description
    if (this.name.length > 34) {
      this.name = this.name.slice(0, 31) + '...'
    }
    this.priorityClass = 'priority' + this.priority
  }

  static async getAllFromApi(
    personType: string,
    personId: number,
    year: number,
    month: number
  ) {
    try {
      const result = await configuredAxios.get(
        Action.getUrl(personType, personId, year, month)
      )
      return [result.data.map(o => new this(o)), ApiRequestStatus.ofSuccess()]
    } catch (e) {
      console.error(e)
      return [null, ApiRequestStatus.ofError()]
    }
  }

  private static getUrl(
    personType: string,
    personId: number,
    year: number,
    month: number
  ): string {
    // JS starts to count months from 0 (January), Python from 1
    // therefore `{month+1}` in the URL construction
    return `actions/by-month/?${personType}Id=${personId}&year=${year}&month=${month +
      1}`
  }

  get durationStr(): string {
    const timeFormat = { hour: '2-digit', minute: '2-digit' }
    const spansMultipleDays =
      this.end && this.start.toDateString() !== this.end.toDateString()

    const startTimeStr = this.start.toLocaleTimeString('et-ee', timeFormat)
    const endTimeStr = this.end
      ? this.end.toLocaleTimeString('et-ee', timeFormat)
      : ''
    const startDateStr = this.start.toLocaleDateString('et-ee')
    const endDateStr = this.end ? this.end.toLocaleDateString('et-ee') : ''

    if (!spansMultipleDays && this.timed) {
      if (!this.end) return startTimeStr
      return startTimeStr + ' – ' + endTimeStr
    }
    if (!spansMultipleDays && !this.timed) {
      return ''
    }
    if (spansMultipleDays && this.timed) {
      return `${startTimeStr} (${startDateStr}) – ${endTimeStr} (${endDateStr})`
    }
    // if (spansMultipleDays && !this.timed)
    return `${startDateStr} – ${endDateStr}`
  }

  get employeeNamesString(): string {
    return this.employees.map(e => e.username).join(', ')
  }

  get clientNamesString(): string {
    return this.clients.map(e => e.fullName).join(', ')
  }
}
